import flagFootballCustomFormData from "../sports/flag-football/config/custom-form-data";
import footballCustomFormData from "../sports/football/config/custom-form-data";
import tennisCustomFormData from "../sports/tennis/config/custom-form-data";
import PlayerPosition from "../sports/football/enums/player-position";
import SportCodes from "../sports/common/sport-codes";
import { DefaultTennisConfig } from "../sports/tennis/config/default";
import { PlayerYears } from "../sports/common/enums/player-years";
import DefaultFlagFootballConfig from "../sports/flag-football/config/default";
import DefaultFootballConfig from "../sports/football/config/default";
import { areEqual, deepCopy } from "./common/object-util";
import DefaultCflConfig from "../sports/football/config/default-cfl";
import cflCustomFormData from "../sports/football/config/cfl-custom-form-data";
import DefaultIndoorConfig from "../sports/football/config/default-indoor";
import indoorCustomFormData from "../sports/football/config/indoor-custom-form-data";
import { fromCamelCase } from "./common/sport-util";
import YardConverter from "../sports/common/utils/yard-converter";
import FootballRuleset from "../sports/football/enums/ruleset";
import TennisRuleset from "../sports/tennis/enums/ruleset";

const sportRuleSetMap = {
    [SportCodes.Football]: {
        [FootballRuleset.DefaultFootball.code]: {
            config: DefaultFootballConfig,
            formData: footballCustomFormData,
            name: FootballRuleset.DefaultFootball.name
        },
        [FootballRuleset.Cfl.code]: {
            config: DefaultCflConfig,
            formData: cflCustomFormData,
            name: FootballRuleset.Cfl.name
        },
        [FootballRuleset.IndoorFootball.code]: {
            config: DefaultIndoorConfig,
            formData: indoorCustomFormData,
            name: FootballRuleset.IndoorFootball.name
        }
    },
    [SportCodes.FlagFootball]: {
        [FootballRuleset.DefaultFlagFootball.code]: {
            config: DefaultFlagFootballConfig,
            formData: flagFootballCustomFormData,
            name: FootballRuleset.DefaultFlagFootball.name
        }
    },
    [SportCodes.Tennis]: {
        [TennisRuleset.DefaultTennis.code]: {
            config: DefaultTennisConfig,
            formData: tennisCustomFormData,
            name: TennisRuleset.DefaultTennis.name
        }
    },
    [SportCodes.WomenTennis]: {
        [TennisRuleset.DefaultTennis.code]: {
            config: DefaultTennisConfig,
            formData: tennisCustomFormData,
            name: TennisRuleset.DefaultTennis.name
        }
    }
};

const defaultRuleSetIdMap = {
    [SportCodes.Football]: FootballRuleset.DefaultFootball.code,
    [SportCodes.FlagFootball]: FootballRuleset.DefaultFlagFootball.code,
    [SportCodes.WomenTennis]: TennisRuleset.DefaultTennis.code,
    [SportCodes.Tennis]: TennisRuleset.DefaultTennis.code
};

const footballPositions = Object.values(PlayerPosition).sort((a, b) => {
    return a.code.localeCompare(b.code);
});

const playerPositions = {
    [SportCodes.FlagFootball]: footballPositions,
    [SportCodes.Football]: footballPositions
};

class SportService {
    resolveDefaultConfig(sportId, ruleSetId) {
        return deepCopy(sportRuleSetMap[sportId]?.[ruleSetId]?.config) || {};
    }

    resolveCustomFormData(sportId, ruleSetId) {
        return deepCopy(sportRuleSetMap[sportId]?.[ruleSetId]?.formData) || {};
    }

    getPlayerPositions(sportId) {
        return playerPositions[sportId];
    }

    getPlayerYears() {
        return Object.values(PlayerYears);
    }

    getRuleSetOptions(sportId) {
        return Object.keys(sportRuleSetMap[sportId]).map((id) => ({
            id,
            name: sportRuleSetMap[sportId][id].name
        }));
    }

    getDefaultRuleSetId(sportId) {
        return defaultRuleSetIdMap[sportId];
    }

    // Human-readable value for the change summary. Arrays of markers/areas
    // stringify to "[object Object]" by default, so format them explicitly.
    // When fmtPos is provided (marker/position fields), yard positions render
    // as H{yard}/V{yard}; field areas as "H0-H10 type", plain arrays joined.
    formatSummaryValue(value, fmtPos) {
        let result = value;
        if (typeof value === "boolean") {
            result = value ? "Yes" : "No";
        } else if (Array.isArray(value)) {
            result = value
                .map((v) => this.formatSummaryValue(v, fmtPos))
                .join(", ");
        } else if (value && typeof value === "object") {
            if (value.start !== undefined && value.end !== undefined) {
                const range = fmtPos
                    ? `${fmtPos(value.start)}-${fmtPos(value.end)}`
                    : `${value.start}-${value.end}`;
                result = value.type ? `${range} ${value.type}` : range;
            } else {
                result = Object.values(value).join(" ");
            }
        } else if (fmtPos && typeof value === "number") {
            result = fmtPos(value);
        }
        return result;
    }

    generateChangeSummary(sportId, ruleSetId, oldConfig, newConfig) {
        const customFormData = this.resolveCustomFormData(sportId, ruleSetId);
        const changes = {};

        const fieldLength = newConfig.general?.fieldLength;
        const converter = fieldLength ? new YardConverter(fieldLength) : null;
        const formatPosition = (pos) => {
            if (!converter || typeof pos !== "number") {
                return pos;
            }
            const { teamSide, yard } = converter.convertFromFieldPosition(pos);
            return `${teamSide}${yard}`;
        };
        // Only marker/position fields render yard positions as H/V yards.
        const positionalFields = ["firstDownYardSet", "fieldAreaSet"];

        Object.keys(newConfig).forEach((key) => {
            Object.keys(newConfig[key]).forEach((childKey) => {
                const oldValue = oldConfig[key][childKey];
                const newValue = newConfig[key][childKey];

                if (!changes[key]) changes[key] = [];
                if (!areEqual(oldValue, newValue)) {
                    const formData = customFormData[key][childKey];
                    const title = formData?.title || fromCamelCase(childKey);
                    let displayValue = this.formatSummaryValue(
                        newValue,
                        positionalFields.includes(childKey)
                            ? formatPosition
                            : undefined
                    );

                    if (formData?.options) {
                        if (formData.multiselect) {
                            displayValue = newValue
                                .map(
                                    (code) =>
                                        formData.options.find(
                                            (o) => o.code === code
                                        )?.name
                                )
                                .join(", ");
                        } else {
                            displayValue = formData.options.find(
                                ({ code }) => code === newValue
                            )?.name;
                        }
                    }

                    changes[key].push({
                        setting: title,
                        message: `Has been ${
                            displayValue
                                ? `changed to '${displayValue}'`
                                : "cleared"
                        }`
                    });
                }
            });
        });

        return changes;
    }
}

export default new SportService();
